'use client';

import ActionButton from '@/components/form/ActionButton/ActionButton';
import FormFile from '@/components/form/FormFile/FormFile';
import FormSelect from '@/components/form/FormSelect/FormSelect';
import FormText from '@/components/form/FormText/FormText';
import FormTextArea from '@/components/form/FormTextArea/FormTextArea';
import { swal } from '@/lib/sweetalert';
import { yup } from '@/lib/yup';
import { useGlobalLoadingAtom } from '@/state';
import layoutStyles from '@/styles/modules/layout.module.scss';
import { pickErrorMessages } from '@/utils/functions';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { type Control, useForm } from 'react-hook-form';
import { useContactAtom } from '../../state';
import type { ContactCategory, ContactFormValues } from '../../types';
import styles from './ContactForm.module.scss';

type Props = {
  name?: string;
  email?: string;
  categories: Array<ContactCategory>;
};

const formScheme = yup.object().shape({
  selectCategoryId: yup
    .number()
    .required()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  name: yup.string().required(),
  email: yup.string().email().required(),
  description: yup.string().required(),
});

const ContactForm = ({ categories, email, name }: Props) => {
  const router = useRouter();
  const [_contactValue, setContactValue] = useContactAtom();
  const [, startGlobalLoading, , forceStopGlobalLoading] = useGlobalLoadingAtom();
  const isLoggedIn = email !== '';
  const minFileInputCount = 1;
  const maxFileInputCount = 5;
  const [fileInputCount, setFileInputCount] = useState(minFileInputCount);
  const { handleSubmit, getValues, control, setValue } = useForm<ContactFormValues>({
    mode: 'onChange',
    defaultValues: {
      selectCategoryId: undefined,
      name: name,
      email: email,
      description: '',
      file0: null,
      file1: null,
      file2: null,
      file3: null,
      file4: null,
    },
    resolver: yupResolver(formScheme),
    shouldUnregister: true,
  });

  const fileForm = (formNum: number, control: Control<ContactFormValues>) => {
    return [...Array(formNum)].map((_, i) => (
      <FormFile
        // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
        key={i}
        label={i === 0 ? '添付画像（JPG/PNG/GIF 2MBまで）最大5ファイル' : ''}
        name={`file${i}`}
        control={control}
        onFileChange={() => handleFileChange(i)}
        onFileDelete={() => handleFileDelete(i)}
      />
    ));
  };

  //ファイルが選択されたときの処理
  const handleFileChange = (index: number) => {
    if (index === fileInputCount - 1 && fileInputCount < maxFileInputCount) {
      setFileInputCount((prevCount) => prevCount + 1);
    }
  };

  const handleFileDelete = (deletedIndex: number) => {
    //選択されているファイルをすべて保持しておき先頭から順番に詰めていく
    const selectedFiles: File[] = [];
    for (let i = 0; i < maxFileInputCount; i++) {
      if (i === deletedIndex) {
        continue;
      }
      const file = getValues(`file${i}` as const);
      if (file instanceof File) {
        selectedFiles.push(file);
      }
    }
    for (let i = 0; i < maxFileInputCount; i++) {
      setValue(`file${i}` as const, null);
    }
    selectedFiles.forEach((file, newIndex) => {
      if (newIndex < maxFileInputCount) {
        setValue(`file${newIndex}` as const, file, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    });
    const newCount =
      selectedFiles.length === 0 ? minFileInputCount : Math.min(maxFileInputCount, selectedFiles.length + 1);
    setFileInputCount(newCount);
  };

  const getFileFormVal = (formNum: number) => {
    return [...Array(formNum)].map((_, i) => getValues(`file${i}`)).filter((f): f is File => f != null);
  };

  const getSelectCategory = (selectCategoryId: number) => {
    return categories.find((category) => category.id === selectCategoryId) as ContactCategory;
  };

  const submit = async () => {
    try {
      startGlobalLoading();
      setContactValue({
        selectCategory: getSelectCategory(Number(getValues('selectCategoryId'))),
        name: getValues('name'),
        email: getValues('email'),
        description: getValues('description'),
        files: getFileFormVal(fileInputCount),
      });
      router.push('/contact/confirm');
    } catch (error) {
      forceStopGlobalLoading();
      await swal.messages({ messages: pickErrorMessages(error) });
    }
  };

  const submitError = async () => {
    await swal.invalid();
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <div className={layoutStyles.formWrapper}>
        <FormText label='名前' name='name' control={control} required isPii placeholder='お名前' />
        <FormText
          label='メールアドレス'
          name='email'
          control={control}
          readOnly={isLoggedIn}
          required
          isPii
          placeholder='メールアドレス'
        />
        <FormSelect
          label='問い合わせ項目'
          name='selectCategoryId'
          items={[
            { value: '', text: '選択してください' },
            ...categories.map((item) => ({
              value: item.id,
              text: item.name,
            })),
          ]}
          control={control}
          required
          outlined={true}
        />
        <div className={styles.fileInputContainer}>{fileForm(fileInputCount, control)}</div>
        <div className={styles.textAreaContainer}>
          <FormTextArea
            label='問い合わせ内容'
            name='description'
            control={control}
            required
            outlined={true}
            placeholder='お問い合わせ内容を入力してください'
            isPii
          />
        </div>
      </div>
      <div className={styles.actionButtonContainer}>
        <ActionButton onClick={handleSubmit(submit, submitError)} color='white' bgColor='primary' size='xLarge'>
          確認する
        </ActionButton>
      </div>
    </form>
  );
};
export default ContactForm;
